import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@mailflow/ui';
import { api } from '../lib/api.js';

interface SuppressContactDialogProps {
  contactId: string;
  email: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export function SuppressContactDialog({
  contactId,
  email,
  onClose,
  onSuccess,
}: SuppressContactDialogProps) {
  const queryClient = useQueryClient();
  const [targetScope, setTargetScope] = useState<'email' | 'domain'>('email');
  const [reason, setReason] = useState<'manual' | 'unsubscribe' | 'no_interest'>('manual');
  const [note, setNote] = useState('');

  const domain = email.split('@')[1] ?? email;

  const suppress = useMutation({
    mutationFn: () =>
      api.post(`/api/contacts/${contactId}/suppress`, {
        targetScope,
        reason,
        note: note.trim() || null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      void queryClient.invalidateQueries({ queryKey: ['suppressions'] });
      if (onSuccess) onSuccess();
      onClose();
    },
  });

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card dialog" role="dialog" aria-modal="true" aria-label="Auf Sperrliste setzen">
        <div className="card__body stack">
          <h2 style={{ fontSize: 'var(--text-lg)' }}>Auf Sperrliste setzen</h2>
          <p className="text-sm muted">
            Gesperrte Adressen oder Domains erhalten unter keinen Umständen mehr E-Mails über das System.
          </p>

          {suppress.isError && (
            <div className="alert" data-tone="danger" role="alert">
              {suppress.error instanceof Error ? suppress.error.message : 'Sperren fehlgeschlagen.'}
            </div>
          )}

          <div className="field">
            <label className="field__label">Was soll gesperrt werden?</label>
            <div className="cluster" style={{ gap: 'var(--space-4)', marginTop: 'var(--space-1)' }}>
              <label className="check">
                <input
                  type="radio"
                  name="scope"
                  checked={targetScope === 'email'}
                  onChange={() => setTargetScope('email')}
                />
                Einzelne Adresse (<code>{email}</code>)
              </label>
              <label className="check">
                <input
                  type="radio"
                  name="scope"
                  checked={targetScope === 'domain'}
                  onChange={() => setTargetScope('domain')}
                />
                Ganze Domain (<code>@{domain}</code>)
              </label>
            </div>
          </div>

          <div className="field">
            <label className="field__label">Sperrkategorie (Grund)</label>
            <select
              className="input"
              value={reason}
              onChange={(e) => setReason(e.target.value as any)}
            >
              <option value="manual">1. Manuelle Sperre (Intern / Team)</option>
              <option value="unsubscribe">2. Abgemeldet (Unsubscribe)</option>
              <option value="no_interest">3. Kein Interesse (Outreach / Opt-Out)</option>
            </select>
          </div>

          <div className="field">
            <label className="field__label">Notiz (Optional)</label>
            <textarea
              className="input"
              rows={2}
              placeholder="Begründung oder Notiz erfassen …"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          <div className="dialog__actions">
            <Button variant="secondary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              onClick={() => suppress.mutate()}
              loading={suppress.isPending}
            >
              Auf Sperrliste setzen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
